import * as React from 'react';
import moment from 'moment';
import propz from 'propz';
import classNames from 'classnames';
import { SchoolEvent, Participant } from 'Src/models/event';
import { EVENT_STATUS_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/event';
import { CRICKET_RESULT_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/cricket';
import { getEventStatus } from 'Src/helpers/accessor/accessor';
import { getParticipantTeamResult } from 'Src/helpers/event/event';
import { isCricket } from 'Src/helpers/sport/cricket';

interface Props {
  event: SchoolEvent;
  participants: Participant[];
}

function participantName(participant: Participant): string {
  return (
    propz.get(participant, ['team', 'name']) ||
    propz.get(participant, ['house', 'name']) ||
    propz.get(participant, ['school', 'name']) ||
    'Participant'
  );
}

function participantLogo(participant: Participant): string {
  return propz.get(participant, ['house', 'pic']) || propz.get(participant, ['school', 'pic']) || '';
}

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase();
}

export function hasEventResults(event: SchoolEvent): boolean {
  const results = event.results || ({} as any);
  return Boolean(
    propz.get(results, ['teamScore', 'length'], 0) ||
      propz.get(results, ['schoolScore', 'length'], 0) ||
      propz.get(results, ['houseScore', 'length'], 0) ||
      propz.get(results, ['individualScore', 'length'], 0)
  );
}

export function EventHeadToHeadFixture({ event, participants }: Props) {
  const home = participants[0];
  const away = participants[1];
  const matchResult = event.matchResult;
  const showScore = Boolean(matchResult && matchResult.regulation) || hasEventResults(event);
  const eventStatus = getEventStatus(event);
  const isFinished = eventStatus === EVENT_STATUS_SERVER_TO_CLIENT_MAPPING.FINISHED;
  const isCancelled = eventStatus === EVENT_STATUS_SERVER_TO_CLIENT_MAPPING.CANCELED;
  const cricketOutcome = propz.get(event, ['results', 'cricketResult', 'result']);
  const cricketOutcomeText = cricketOutcome ? CRICKET_RESULT_SERVER_TO_CLIENT_MAPPING[cricketOutcome] : '';
  const resolvedScore = matchResult && matchResult.resolution === 'EXTRA_TIME' && matchResult.extraTime
    ? matchResult.extraTime
    : matchResult && matchResult.regulation;
  const homeResult = resolvedScore ? resolvedScore.home : showScore ? getParticipantTeamResult(event, home) : '';
  const awayResult = resolvedScore ? resolvedScore.away : showScore ? getParticipantTeamResult(event, away) : '';
  const resultCaption = matchResult && matchResult.resolution === 'SHOOTOUT' && matchResult.shootout
    ? `Penalties ${matchResult.shootout.home}–${matchResult.shootout.away}`
    : matchResult && matchResult.resolution === 'EXTRA_TIME'
    ? 'After extra time'
    : 'Full time';

  const renderCrest = (participant: Participant) => {
    const name = participantName(participant);
    const logo = participantLogo(participant);
    const colors = propz.get(participant, ['house', 'colors'], []);
    const style = colors.length ? { backgroundColor: colors[0] } : undefined;

    return (
      <span className="eEventHero__crest" style={style}>
        {logo ? <img src={logo} alt="" /> : <span>{initials(name)}</span>}
      </span>
    );
  };

  return (
    <div className="eEventHero__fixture">
      <div className="eEventHero__side">
        {renderCrest(home)}
        <div className="eEventHero__sideName">{participantName(home)}</div>
        <span className="eEventHero__sideBadge eEventHero__sideBadge--home">Home</span>
      </div>
      <div className="eEventHero__score">
        {showScore ? (
          <div
            className={classNames('eEventHero__scoreValue', { 'eEventHero__scoreValue--cricket': isCricket(event) })}
          >
            <span>{homeResult}</span>
            <span className="eEventHero__scoreDash">–</span>
            <span>{awayResult}</span>
          </div>
        ) : (
          <div className="eEventHero__versus">{isFinished ? 'No results yet' : 'vs'}</div>
        )}
        <div className="eEventHero__scoreCaption">
          {isCancelled
            ? 'Cancelled'
            : isCricket(event) && cricketOutcomeText
            ? cricketOutcomeText
            : isFinished
            ? resultCaption
            : moment(event.startTime).format('HH:mm')}
        </div>
      </div>
      <div className="eEventHero__side">
        {renderCrest(away)}
        <div className="eEventHero__sideName">{participantName(away)}</div>
        <span className="eEventHero__sideBadge">Away</span>
      </div>
    </div>
  );
}
